import { Injectable, NgZone } from '@angular/core';

/** Minimal structural type for the Amplitude BrowserClient we wrap. */
interface TrackClient {
    track(...args: unknown[]): unknown;
}

interface BufferedEvent {
    args: unknown[];
    time: number;
}

/**
 * Buffers Amplitude `track` events until we know who the user is.
 *
 * Pure1 sends some analytics events very early in start-up — before the app has
 * finished working out who the signed-in user is. Amplitude stamps the user
 * properties (domain, email, org, …) onto each event at the moment it is sent and
 * never fills them in afterwards, so any event sent before we identify the user
 * is recorded with those properties as `(none)` — permanently.
 *
 * To prevent that, this service holds every outgoing `track` call in a small
 * in-memory queue and releases it only once the app has identified the user (the
 * identify call triggers {@link flush}). The replayed events go out *after* the
 * identify, so Amplitude attaches the correct user properties to them.
 *
 * NOTE: We *may* be able to simplify things and remove this completely once we
 * get rid of the Angulartics hook. Worth investigating again in the future.
 */
@Injectable({ providedIn: 'root' })
export class AmplitudeBufferService {
    /**
     * Max events held before identity is known; the oldest is dropped beyond
     * this so an anonymous session that never logs in cannot grow unbounded.
     */
    static readonly MAX_BUFFER = 100;

    private identityReady = false;
    private buffer: BufferedEvent[] = [];
    private realTrack: ((...args: unknown[]) => unknown) | null = null;

    constructor(private readonly ngZone: NgZone) {}

    /**
     * Wrap `client.track` so events are buffered until `flush()` is called.
     * Idempotent: a second call is a no-op (keeps the original real track).
     */
    install(client: TrackClient): void {
        if (this.realTrack) {
            return;
        }
        const track = client.track.bind(client);
        // Track outside the Angular zone: each call schedules the SDK's timeline and flush timers and
        // its fetch, which would otherwise run a full change detection apiece, for an analytics call
        // that changes nothing on screen.
        this.realTrack = (...args: unknown[]): unknown => this.ngZone.runOutsideAngular(() => track(...args));
        client.track = (...args: unknown[]): unknown => this.gatedTrack(args);
    }

    /**
     * Mark identity ready and replay buffered events in FIFO order, stamping
     * each with its captured `time` and the identified `userId`. Safe to call
     * if `install()` never ran.
     * @param userId the identified user id to attribute flushed events to
     */
    flush(userId: string): void {
        this.identityReady = true;
        if (!this.realTrack) {
            return;
        }
        const held = this.buffer;
        this.buffer = [];
        for (const event of held) {
            // SDK track() is (eventInput, eventProperties, eventOptions); replay those three.
            const [eventInput, eventProperties, eventOptions] = event.args;
            this.realTrack(eventInput, eventProperties, {
                ...((eventOptions as Record<string, unknown> | undefined) ?? {}),
                time: event.time,
                user_id: userId,
            });
        }
    }

    private gatedTrack(args: unknown[]): unknown {
        if (this.identityReady && this.realTrack) {
            return this.realTrack(...args);
        }
        const eventOptions = args[2] as { time?: number } | undefined;
        const time = eventOptions?.time ?? Date.now();
        this.buffer.push({ args, time });
        if (this.buffer.length > AmplitudeBufferService.MAX_BUFFER) {
            this.buffer.shift();
        }
        // Match the SDK's "no-op" void result shape so callers reading `.promise` don't break.
        return { promise: Promise.resolve() };
    }
}
